import { startTransition, useRef } from "react"
import type { ErrorInfo, FC, ReactElement, RefObject } from "react"

import { AxiosError } from "axios"
import { AnimatePresence, motion } from "framer-motion"
import { ErrorBoundary as ReactErrorBoundary, useErrorBoundary } from "react-error-boundary"
import { useBoolean, useClickAway } from "react-use"

import { DefaultAvatarIcon, OwlSadImage } from "@/3514/assets"
import {
    Button,
    EButtonVariant,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ErrorHint,
    Typography
} from "@/3514/components"
import { EPath, browserHistory } from "@/3514/constants"
import {
    buildCssClassName,
    captureSentryRootError,
    getLinkDecorationTwClasses,
    getTransitionTwClasses,
    withPressHandler
} from "@/3514/utils"
import { CARE_TEAM_EMAIL } from "@/constants"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyCallback } from "@/shared/types/functions"
import WiserLogo from "@/svgs/WiserLogo"

const classes = {
    fallback: {
        wrapper: (): string => "flex flex-col h-[100dvh] gap-y-[100px]",
        headerContainer: (): string => "relative w-full flex items-center justify-between py-[15px] px-[15px]",
        logo: (): string =>
            buildCssClassName(
                "rounded-full",
                "w-fit",
                "p-[3px]",
                "border-transparent",
                "border-[1px]",
                "hover:border-accentOld",
                "focus-visible:border-accentOld",
                getTransitionTwClasses("all", 200)
            ),
        avatar: (): string =>
            buildCssClassName(
                "flex",
                "items-center",
                "justify-center",
                "w-full",
                "border-[1px]",
                "p-[3px]",
                "rounded-full",
                "border-transparent",
                "hover:border-gray-300",
                "focus:border-gray-300"
            ),
        dropdown: ({ isShown }: { isShown: boolean }): string =>
            buildCssClassName(
                "absolute",
                "rounded-[10px]",
                "top-[15px]",
                "right-[55px]",
                isShown ? "opacity-[1]" : "opacity-[0] pointer-events-none",
                getTransitionTwClasses("all", 200)
            ),
        dropdownItem: (): string =>
            buildCssClassName(
                "p-[10px]",
                "bg-blue-600",
                "rounded-[10px]",
                "hover:bg-blue-500",
                "focus-visible:bg-blue-500",
                getTransitionTwClasses("all", 200)
            ),
        container: (): string => "flex flex-1 flex-col items-center justify-start gap-y-[50px] pb-[100px]",
        imgWrapper: (): string => "rounded-full flex items-center justify-center bg-blue-600 w-[185px] h-[185px]",
        img: (): string => "[scale:0.9]",
        link: (): string =>
            buildCssClassName(
                "!outline-none",
                "text-center",
                "text-[14px]",
                "heading-[21px]",
                "font-[800]",
                "text-blue-200",
                "hover:text-accent",
                "focus-visible:text-accent",
                "mt-auto",
                getLinkDecorationTwClasses({ color: "accent", offset: 3 })
            )
    }
}

function ErrorFallback(): ReactElement {
    const { t }: IUseTranslation = useTranslation()

    const triggerReload: TEmptyCallback = (): ReactElement => void window.location.reload()

    const { resetBoundary }: ReturnType<typeof useErrorBoundary> = useErrorBoundary()

    const [isAvatarClicked, setIsAvatarClicked]: ReturnType<typeof useBoolean> = useBoolean(false)

    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    useClickAway(containerRef, (): void => setIsAvatarClicked(false))

    const handleAvatarClick: TEmptyCallback = (): void =>
        startTransition((): void => setIsAvatarClicked(!isAvatarClicked))

    const handleLogoutAction: TEmptyCallback = (): void => {
        resetBoundary()

        browserHistory.replace(EPath.Login({ isErrorReset: true }))
    }

    return (
        <AnimatePresence initial mode="sync">
            <motion.div
                key={window.location.href}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeIn" }}
                className={classes.fallback.wrapper()}
            >
                <div className={classes.fallback.headerContainer()}>
                    <button
                        tabIndex={0}
                        className={classes.fallback.logo()}
                        aria-label={t("Logo")}
                        onClick={withPressHandler(triggerReload)}
                        onKeyDown={withPressHandler(triggerReload)}
                    >
                        <WiserLogo width={30} height={30} />
                    </button>

                    <div ref={containerRef} hidden={window.location.pathname.includes("login")}>
                        <button
                            className={classes.fallback.avatar()}
                            tabIndex={0}
                            data-toggle="dropdown"
                            aria-expanded={isAvatarClicked}
                            aria-label={t("participantSide.header.dropdown.opener.ariaLabel")}
                            onClick={withPressHandler(handleAvatarClick)}
                            onKeyDown={withPressHandler(handleAvatarClick)}
                        >
                            <DefaultAvatarIcon width={25} height={25} alt={t("common.avatarFallback.alt")} />
                        </button>

                        <div className={classes.fallback.dropdown({ isShown: isAvatarClicked })}>
                            <button
                                tabIndex={0}
                                aria-label={t("common.signOut")}
                                className={classes.fallback.dropdownItem()}
                                onClick={withPressHandler(handleLogoutAction)}
                                onKeyDown={withPressHandler(handleLogoutAction)}
                            >
                                <Typography
                                    text={t("common.signOut")}
                                    tag={ETypographyTag.Span}
                                    size={ETypographySize.Small}
                                    color={ETypographyColor.Dark}
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2, delay: 0.1, ease: "easeInOut" }}
                    className={classes.fallback.container()}
                >
                    <ErrorHint isVisible />

                    <div className={classes.fallback.imgWrapper()}>
                        <OwlSadImage className={classes.fallback.img()} />
                    </div>

                    <Button
                        isCentered
                        variant={EButtonVariant.Outlined}
                        onClick={withPressHandler(triggerReload)}
                        onKeyDown={withPressHandler(triggerReload)}
                    >
                        {t("errorBoundary.button")}
                    </Button>

                    <a href={CARE_TEAM_EMAIL} className={classes.fallback.link()}>
                        {t("Contact Wiser Care Team")}
                    </a>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}

type _TProps = { children: ReactElement }
const RootErrorBoundary: FC<_TProps> = ({ children }: _TProps): ReactElement => {
    function handleError(error: Error | AxiosError, info: ErrorInfo): void {
        captureSentryRootError(error, info)
    }

    return (
        <ReactErrorBoundary
            resetKeys={[window.location.pathname, window.location.search]}
            onError={handleError}
            fallback={<ErrorFallback />}
        >
            {children}
        </ReactErrorBoundary>
    )
}

RootErrorBoundary.displayName = "RootErrorBoundary"

export { RootErrorBoundary }
